import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash, Share2, Copy } from 'lucide-react';
import { useScenarios } from '../hooks/useScenarios';

const ScenarioList = ({ supabase }) => {
  const { scenarios, loading, error, deleteScenario, duplicateScenario } = useScenarios(supabase);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareToken, setShareToken] = useState('');

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this scenario?')) {
      try {
        await deleteScenario(id);
      } catch (error) {
        console.error('Error deleting scenario:', error);
      }
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicateScenario(id);
    } catch (error) {
      console.error('Error duplicating scenario:', error);
      alert('Failed to duplicate scenario. Please try again.');
    }
  };

  const handleShare = (token) => {
    setShareToken(token);
    setShowShareModal(true);
  };

  const handleCopyToClipboard = () => {
    const shareUrl = `https://scan.qr2air.com?token=${shareToken}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        // Optional: Show a toast/notification that the URL was copied
        console.log('URL copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
      });
  };

  return (
    <div className="bg-gray-50">
      <main className="px-0">
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Scenarios</h2>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              Manage your QR code scanning scenarios
            </p>
            <Link
              to="/scenarios/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Scenario
            </Link>
          </div>
        </div>

      {loading ? (
        <div className="text-center py-10">
          <p className="text-gray-500">Loading scenarios...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Error loading scenarios: {error}
              </p>
            </div>
          </div>
        </div>
      ) : scenarios.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500">No scenarios found. Create your first one!</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scenario Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Token
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scenarios.map((scenario) => (
                <tr key={scenario.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{scenario.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{scenario.token}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleShare(scenario.token)}
                      className="text-green-600 hover:text-green-900 mr-4"
                      title="Share scenario"
                    >
                      <Share2 className="h-4 w-4 inline" />
                    </button>
                    <button
                      onClick={() => handleDuplicate(scenario.id)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                      title="Duplicate scenario"
                    >
                      <Copy className="h-4 w-4 inline" />
                    </button>
                    <Link
                      to={`/scenarios/${scenario.id}`}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                      title="Edit scenario"
                    >
                      <Edit className="h-4 w-4 inline" />
                    </Link>
                    <button
                      onClick={() => handleDelete(scenario.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete scenario"
                    >
                      <Trash className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Share Scenario</h3>
            <div className="flex items-center">
              <input
                type="text"
                readOnly
                value={`https://scan.qr2air.com?token=${shareToken}`}
                className="w-full px-3 py-2 border rounded-md mb-3"
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
              <button
                onClick={handleCopyToClipboard}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
};

export default ScenarioList;